import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import { keepPreviousData } from "@tanstack/react-query";
import type { AppRouter } from "../../server/routers";
import { trpc } from "@/lib/trpc";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { DEFAULT_SUCCESS_GRAPHIC } from "@/lib/successGraphic";
import {
  Search,
  ExternalLink,
  Sparkles,
  FileText,
  Tag as TagIcon,
  ShieldCheck,
  Play,
  X,
  ChevronDown,
  ChevronUp,
  Download,
  Check,
  MapPin,
  CheckCircle2,
  Film,
  ArrowRight,
  Loader2,
  RefreshCw,
  FolderPlus,
  Users,
  AlertTriangle,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useUploadContext } from "@/contexts/UploadContext";
import {
  downloadBundle,
  type BundleProgress,
  type BundleResult,
} from "@/lib/bundleDownload";
import { Button } from "@/components/ui/button";
import { useSelectionCart, type SelectedItem } from "@/lib/selectionCart";
import { cn, matchesSearch } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// SelectedItem moved to @/lib/selectionCart — that module now owns
// the cart state with localStorage persistence so the user's
// selections survive an accidental refresh.

const PAGE_SIZE = 20;
const MAX_RESULTS = 200;

// ─── Single-clip playback coordination ───────────────────────────────
// A search page can show many clips at once. Two behaviors keep playback
// sane: (1) only one clip plays at a time — starting one pauses the rest;
// (2) a clip pauses itself once it scrolls out of view. There's no media
// context in the app, so we coordinate through a tiny module-level
// registry of the currently-mounted <video>/<audio> elements.
const mountedMedia = new Set<HTMLMediaElement>();

function pauseOtherMedia(current: HTMLMediaElement): void {
  for (const el of mountedMedia) {
    if (el !== current && !el.paused) el.pause();
  }
}

/**
 * Wires a search-result media element into the playback coordination:
 * registers it while mounted, pauses it when it scrolls out of view via
 * an IntersectionObserver, and returns an onPlay handler that pauses
 * every other clip. Uses a callback ref (not useRef + useEffect) so it
 * fires correctly for the conditionally-rendered <video>/<audio>
 * branches, and re-attaches cleanly if the element swaps.
 */
function useCoordinatedMedia() {
  const elRef = useRef<HTMLMediaElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setMediaRef = useCallback((el: HTMLMediaElement | null) => {
    if (elRef.current) {
      observerRef.current?.disconnect();
      observerRef.current = null;
      mountedMedia.delete(elRef.current);
    }
    elRef.current = el;
    if (!el) return;
    mountedMedia.add(el);
    // Pause once the clip is ~90% scrolled off. We never auto-resume on
    // re-entry — leaving the viewport stops playback, it doesn't toggle.
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && !el.paused) el.pause();
        }
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    observerRef.current = io;
  }, []);

  const onPlay = useCallback((e: React.SyntheticEvent<HTMLMediaElement>) => {
    pauseOtherMedia(e.currentTarget);
  }, []);

  return { setMediaRef, onPlay };
}

/**
 * Latches true once a card scrolls within `rootMargin` of the viewport, and
 * never flips back. We use this to defer mounting each card's media `src`
 * until it's actually near the screen. Before this, every result card mounted
 * a live <video preload="metadata"> immediately, so a long results page fired
 * dozens of concurrent range requests through the single shared-Drive proxy —
 * they starved the one clip the user actually clicked, which is what made
 * playback feel slow to start. Gating to the viewport means only on-screen
 * clips fetch. The generous rootMargin preloads a screen ahead so scrolling
 * still feels instant, and latching (disconnect on first hit) means scrolling
 * a clip away and back never re-tears-down or re-fetches its player.
 */
function useNearViewport<T extends HTMLElement>(rootMargin = "600px") {
  const ref = useRef<T | null>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === "undefined") {
      // Safety net for environments without IO — just load eagerly.
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, rootMargin]);
  return { ref, near };
}

/**
 * True when a bundle result has at least one failure we can auto-retry —
 * i.e. a video/audio clip (it has a clipId that maps to
 * /api/footage/stream?fileId=). Consent-PDF failures don't qualify (no clip
 * id); the Open-in-Drive link on each covers those. `null` result (cancelled
 * picker) is not retryable.
 */
function retryableFailures(result: BundleResult | null): boolean {
  return (
    !!result &&
    result.failures.some((f) => f.kind !== "consent" && f.clipId !== null)
  );
}

/**
 * Location filter chips for the Footage Search input card. Same exact
 * color values as the patient-picker Location chips in
 * GDriveFolderPicker.tsx / PatientStep.tsx so the visual vocabulary
 * stays consistent across the app.
 */
const LOCATION_CHIP_OPTIONS: Array<{
  region: "Iowa" | "Minnesota" | "Colorado";
  dotClass: string;
  selectedBg: string;
  selectedRing: string;
}> = [
  {
    region: "Iowa",
    dotClass: "bg-[#5B7C99]",
    selectedBg: "bg-[#5B7C99]",
    selectedRing: "ring-[#5B7C99]",
  },
  {
    region: "Minnesota",
    dotClass: "bg-[#6A5ACD]",
    selectedBg: "bg-[#6A5ACD]",
    selectedRing: "ring-[#6A5ACD]",
  },
  {
    region: "Colorado",
    dotClass: "bg-[#1b6e4a]",
    selectedBg: "bg-[#1b6e4a]",
    selectedRing: "ring-[#1b6e4a]",
  },
];

/**
 * Natural-language search across uploaded footage. Server runs a hybrid
 * Postgres FTS + pgvector query (reciprocal rank fusion) over title,
 * activity tags, topic tags, Pegasus-generated descriptions, and Whisper
 * transcripts. All authenticated users see all footage (Private + Home-Use
 * Only excluded); role only gates client-side UI like the Admin Menu.
 */
export default function FootageSearch() {
  useAuth({ redirectOnUnauthenticated: true });

  const [input, setInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // Patient B-Roll permission filters — four independent toggles forming
  // a 2x2 matrix over (patient_permission, has_special_instructions).
  // Default lands in the safest cell: Pre-Approved patients without any
  // Special Instructions. Provider A-Roll is unaffected by these — it
  // always passes (no patient consent to gate on). Private + Home Use
  // Only footage is never indexed in the first place, so it has no
  // filter checkbox.
  const [patientFilters, setPatientFilters] = useState({
    preApprovedNoSI: true,
    preApprovedSI: false,
    cbpNoSI: false,
    cbpSI: false,
  });
  // Sort mode: pure relevance (default) or RRF damped by usage_count
  // so clips that have been bundled before fall behind fresh ones.
  const [boostLesserUsed, setBoostLesserUsed] = useState(false);
  // Whether the next bundle download includes the DB-sourced sidecars
  // (.description.txt / .txt / .srt). Default OFF — most downloads just
  // want the videos; sidecars are opt-in. Videos, consent forms, and the
  // manifest download regardless.
  const [includeSidecars, setIncludeSidecars] = useState(false);
  // Subject-kind filter — three-state segmented control. 'all' (default)
  // shows everything; 'patient' restricts to B-Roll; 'provider'
  // restricts to A-Roll. Mirrors the Tag Library's Activity/Topic toggle.
  const [subjectKindFilter, setSubjectKindFilter] = useState<
    "all" | "patient" | "provider"
  >("all");
  // Page-level mode. "search" is the default (free-text relevance
  // search across the library); "sidecars" flips the page into a
  // patient-picker view that downloads sidecars + consents for a
  // single patient. Switching modes preserves subjectKindFilter so
  // bouncing in and out of Sidecars doesn't reset the user's
  // search-filter pick.
  const [pageMode, setPageMode] = useState<"search" | "sidecars">("search");
  // Sidecars-mode state. The picker drills down through:
  //   sidecarsSubject (patient | provider)
  //     → pickerLocation
  //       → pickerPatientFolderId (patient flow)         → download
  //       → pickerProviderFolderId (provider flow)
  //         → pickerTopic (provider flow continued)      → download
  // pickerSearch is shared between the patient + provider typeaheads
  // (only one is visible at a time); pickerTopicSearch filters the
  // topic dropdown separately so the topic step doesn't lose its
  // typed filter when the user adjusts the patient/provider search.
  const [sidecarsSubject, setSidecarsSubject] = useState<
    "patient" | "provider"
  >("patient");
  const [pickerLocation, setPickerLocation] = useState<
    "Iowa" | "Minnesota" | "Colorado" | null
  >(null);
  const [pickerPatientFolderId, setPickerPatientFolderId] = useState<
    string | null
  >(null);
  const [pickerProviderFolderId, setPickerProviderFolderId] = useState<
    string | null
  >(null);
  const [pickerTopic, setPickerTopic] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerTopicSearch, setPickerTopicSearch] = useState("");
  // Location filter — empty Set means "all locations" (no filter).
  // Selecting one or more region chips narrows the result set to
  // clips with a matching region column. Multi-select.
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set());
  const toggleRegion = (region: string) => {
    setSelectedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };
  // "Filmed by" filter — empty Set means "any uploader" (no filter).
  // Values are user googleIds. Multi-select.
  const [selectedFilmers, setSelectedFilmers] = useState<Set<string>>(new Set());
  const toggleFilmer = (googleId: string) => {
    setSelectedFilmers((prev) => {
      const next = new Set(prev);
      if (next.has(googleId)) next.delete(googleId);
      else next.add(googleId);
      return next;
    });
  };
  // Topic filter (A-Roll umbrella topics) — empty Set means "any topic".
  // Values are the video_topic strings. Multi-select.
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  };
  // Options for the two new filters. Cheap, cacheable lookups.
  const filmersQuery = trpc.footage.listFilmers.useQuery();
  const topicsQuery = trpc.footage.listTopics.useQuery();
  // How many results the current search returns. Starts at PAGE_SIZE,
  // grows by PAGE_SIZE each "Show more" click, capped at MAX_RESULTS.
  // Resets on query or filter change.
  const [displayedLimit, setDisplayedLimit] = useState(PAGE_SIZE);
  // Accumulating selection — persists across queries, paginations, AND
  // page refreshes via localStorage. The hook owns the Map; we only
  // see toggle / remove / clear actions.
  const {
    selected,
    toggle: toggleSelection,
    addMany: addManyToSelection,
    remove: removeFromSelection,
    clear: clearSelection,
    audioIncluded,
    toggleAudioInclusion,
  } = useSelectionCart();

  // Confirmation state for the "add all session clips" button. Stored
  // rather than fired inline so we can render an AlertDialog when the
  // number of new clips would exceed the confirm threshold (>10).
  // `items` is the pre-mapped SelectedItem list ready for addMany;
  // `newCount` is what the confirmation copy shows to the user.
  const [sessionAddConfirm, setSessionAddConfirm] = useState<{
    items: SelectedItem[];
    newCount: number;
  } | null>(null);
  const [sessionAddLoadingId, setSessionAddLoadingId] = useState<number | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(input.trim()), 300);
    return () => window.clearTimeout(id);
  }, [input]);

  // Reset pagination whenever the search inputs change — a new query
  // shouldn't inherit the previous query's page count.
  // Memoize the regions array so the trpc query's input identity is
  // stable as long as the underlying Set's contents haven't changed.
  // Sorted so [Iowa, Minnesota] and [Minnesota, Iowa] are the same
  // cache key.
  const regionFilterValue = useMemo(
    () => (selectedRegions.size === 0 ? undefined : Array.from(selectedRegions).sort()),
    [selectedRegions],
  );
  const filmedByFilterValue = useMemo(
    () => (selectedFilmers.size === 0 ? undefined : Array.from(selectedFilmers).sort()),
    [selectedFilmers],
  );
  const videoTopicFilterValue = useMemo(
    () => (selectedTopics.size === 0 ? undefined : Array.from(selectedTopics).sort()),
    [selectedTopics],
  );

  // Memoize the filter list sent to the server so the trpc cache key is
  // stable across re-renders. Sort the entries by a deterministic key
  // so the same set of checked boxes always produces the same array
  // ordering.
  const patientPermissionFilters = useMemo(() => {
    const entries: Array<{
      permission: "pre-approved" | "check-before-posting";
      hasSpecialInstructions: boolean;
    }> = [];
    if (patientFilters.preApprovedNoSI)
      entries.push({ permission: "pre-approved", hasSpecialInstructions: false });
    if (patientFilters.preApprovedSI)
      entries.push({ permission: "pre-approved", hasSpecialInstructions: true });
    if (patientFilters.cbpNoSI)
      entries.push({ permission: "check-before-posting", hasSpecialInstructions: false });
    if (patientFilters.cbpSI)
      entries.push({ permission: "check-before-posting", hasSpecialInstructions: true });
    return entries;
  }, [patientFilters]);

  useEffect(() => {
    setDisplayedLimit(PAGE_SIZE);
  }, [
    debouncedQuery,
    patientFilters,
    boostLesserUsed,
    regionFilterValue,
    subjectKindFilter,
    filmedByFilterValue,
    videoTopicFilterValue,
  ]);

  // Search query only fires in search mode. Sidecars mode has no
  // free-text query — the patient picker drives everything.
  const enabled = pageMode === "search" && debouncedQuery.length >= 2;
  const searchQuery = trpc.footage.search.useQuery(
    {
      query: debouncedQuery,
      limit: displayedLimit,
      patientPermissionFilters,
      sortMode: boostLesserUsed ? "lesser-used" : "relevance",
      regions: regionFilterValue as ("Iowa" | "Minnesota" | "Colorado")[] | undefined,
      subjectKindFilter,
      filmedByUserIds: filmedByFilterValue,
      videoTopics: videoTopicFilterValue,
    },
    { enabled, placeholderData: keepPreviousData },
  );

  const results = searchQuery.data ?? [];
  const queryTokens = useMemo(() => tokenize(debouncedQuery), [debouncedQuery]);
  // "Show more" appears only when the server gave us a full page back
  // (suggesting there are more rows behind it) AND we haven't hit the cap.
  const canShowMore = results.length === displayedLimit && displayedLimit < MAX_RESULTS;
  const isFetchingMore = searchQuery.isFetching && !searchQuery.isLoading;

  // tRPC handles for the Phase-4 bundle download flow. The orchestrator
  // (lib/bundleDownload.ts) takes thin async callbacks rather than tRPC
  // bindings directly, keeping it framework-agnostic and easy to test.
  const utils = trpc.useUtils();
  const recordDownloadMutation = trpc.footage.recordBundleDownload.useMutation();

  // "Add all clips from this session" handler — one click on a result
  // card fetches every clip that was uploaded in the same session,
  // filters out anything already in the bundle, and either adds them
  // straight in or (when >10 new) surfaces a confirm dialog first.
  // Uses the current patientPermissionFilters so a session sibling
  // can't leak past the user's live permission scope. Video-kind
  // clips only — audio siblings ride along via the per-video
  // accompanying-audio toggle.
  const handleAddSessionMates = async (hit: SearchHit) => {
    const sessionId = hit.file.sessionId;
    setSessionAddLoadingId(sessionId);
    try {
      const mates = await utils.footage.getSessionMates.fetch({
        sessionId,
        patientPermissionFilters,
      });
      const items: SelectedItem[] = mates.map((m) => ({
        id: m.id,
        title: m.renamedName || m.originalName,
        driveFileId: m.driveFileId,
        subjectKind: m.subjectKind,
        mediaKind: m.mediaKind === "audio" ? "audio" : "video",
      }));
      const newItems = items.filter((i) => !selected.has(i.id));
      if (newItems.length === 0) {
        toast.info("All clips from this session are already in your bundle.");
        return;
      }
      if (newItems.length > 10) {
        setSessionAddConfirm({ items, newCount: newItems.length });
        return;
      }
      addManyToSelection(items);
      toast.success(
        `Added ${newItems.length} clip${newItems.length === 1 ? "" : "s"} from this session to the bundle.`,
      );
    } catch (err) {
      toast.error(
        `Couldn't load session clips: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSessionAddLoadingId(null);
    }
  };
  // Overlay state machine. While a bundle download is in flight the
  // overlay takes over the main content area and shows progress bars;
  // once it finishes it ALWAYS transitions to the "Payload Delivered"
  // screen — even when some files failed — so the user gets a definitive
  // end state instead of being silently dropped back into the search list.
  // On a partial download the success screen shows a prominent error panel
  // (which files failed, an Open-in-Drive link each, and a retry button).
  // The only non-success outcome is a cancelled folder picker (result ===
  // null), which leaves the user on the search list with nothing written.
  type DownloadOverlay =
    | { phase: "downloading"; progress: BundleProgress | null }
    | {
        phase: "success";
        result: BundleResult;
        // Set when there are retryable failures; the success screen shows a
        // "Retry failed downloads" button wired to this. null on a clean
        // run or when nothing can be auto-retried (Drive links cover those).
        onRetry: (() => void) | null;
      };
  const [overlay, setOverlay] = useState<DownloadOverlay | null>(null);

  // Shared landing logic for every downloadBundle() call. `result === null`
  // means the folder picker was cancelled (nothing written) → back to the
  // list. Otherwise always show the delivered screen; clear the cart only
  // on a clean run when the caller opts in (the sidecar flows don't use the
  // cart, so they pass clearOnClean: false to avoid wiping a cart the user
  // built in search mode).
  const settleOverlay = (
    result: BundleResult | null,
    opts: { onRetry: (() => void) | null; clearOnClean: boolean },
  ) => {
    if (!result) {
      setOverlay(null);
      return;
    }
    const clean =
      result.videoFailed === 0 &&
      result.consentFailed === 0 &&
      result.audioFailed === 0;
    setOverlay({
      phase: "success",
      result,
      onRetry: clean ? null : opts.onRetry,
    });
    if (clean && opts.clearOnClean) clearSelection();
  };

  // Re-run the main bundle for just the clips that failed last time. Uses
  // the same download path (a fresh folder pick included — the File System
  // Access picker needs a user gesture, which the retry button provides),
  // scoped to the failed video/audio clip ids so the user isn't
  // re-downloading files that already landed. Consent-PDF failures aren't
  // auto-retried here (they have no clip id); the Drive link on each covers
  // them.
  const handleRetryFailed = async (prev: BundleResult) => {
    const retryIds = Array.from(
      new Set(
        prev.failures
          .filter((f) => f.kind !== "consent" && f.clipId !== null)
          .map((f) => f.clipId as number),
      ),
    );
    if (retryIds.length === 0) {
      toast.info(
        "Nothing to retry automatically — use the Open in Drive links to grab the failed files.",
      );
      return;
    }
    setOverlay({ phase: "downloading", progress: null });
    try {
      const result = await downloadBundle({
        selectedIds: retryIds,
        fetchMetadata: async (idsArg) =>
          utils.footage.bundleMetadata.fetch({ ids: idsArg }),
        recordDownload: async (idsArg) => {
          await recordDownloadMutation.mutateAsync({ ids: idsArg });
        },
        includeSidecars,
        onProgress: (p) => setOverlay({ phase: "downloading", progress: p }),
      });
      settleOverlay(result, {
        onRetry: retryableFailures(result)
          ? () => void handleRetryFailed(result as BundleResult)
          : null,
        clearOnClean: true,
      });
    } catch (err) {
      setOverlay(null);
      throw err;
    }
  };

  const handleDownloadBundle = async () => {
    const ids = Array.from(selected.keys());
    // Expand the selection with accompanying-audio IDs from any video
    // cards where the "Include accompanying audio" checkbox is on. The
    // audio IDs come from the latest search result page's
    // accompanyingAudio[] list — that's how we keep the cart payload
    // small (no need to mirror audio IDs into localStorage; the link
    // is recomputed at download time so a re-analysis doesn't leave
    // stale matches in the user's cart).
    const accompanyingAudioIds: number[] = [];
    const hits = searchQuery.data ?? [];
    for (const hit of hits) {
      if (!selected.has(hit.file.id)) continue;
      if (!audioIncluded(hit.file.id)) continue;
      for (const aud of hit.accompanyingAudio ?? []) {
        accompanyingAudioIds.push(aud.id);
      }
    }
    setOverlay({ phase: "downloading", progress: null });
    try {
      const result = await downloadBundle({
        selectedIds: ids,
        accompanyingAudioIds,
        fetchMetadata: async (idsArg) =>
          utils.footage.bundleMetadata.fetch({ ids: idsArg }),
        recordDownload: async (idsArg) => {
          await recordDownloadMutation.mutateAsync({ ids: idsArg });
        },
        includeSidecars,
        // Always start in download mode. Per-file skip buttons in the
        // download overlay let the user convert individual videos to
        // shortcuts mid-stream when a particular file turns out to be
        // bigger than they want to wait for.
        onProgress: (p) => setOverlay({ phase: "downloading", progress: p }),
      });
      // Always land on the delivered screen (settleOverlay). A clean run
      // clears the cart — the persisted-cart feature is only meaningful
      // pre-download, so once a bundle lands the cart is done. A partial
      // run keeps the cart intact and hands the screen a retry closure
      // scoped to the failed clips; a cancelled picker (result === null)
      // stays on the search list.
      settleOverlay(result, {
        onRetry: retryableFailures(result)
          ? () => void handleRetryFailed(result as BundleResult)
          : null,
        clearOnClean: true,
      });
    } catch (err) {
      setOverlay(null);
      throw err;
    }
  };

  // Patient list — only fetched when in sidecars mode with the
  // Patient sub-subject + a location chosen.
  const patientListQuery = trpc.gdrive.listDrivePatients.useQuery(
    { region: pickerLocation ?? undefined },
    {
      enabled:
        pageMode === "sidecars" &&
        sidecarsSubject === "patient" &&
        pickerLocation !== null,
    },
  );
  // Provider list — same gate but for the Provider sub-subject.
  const providerListQuery = trpc.gdrive.listDriveProviders.useQuery(
    { region: pickerLocation ?? undefined },
    {
      enabled:
        pageMode === "sidecars" &&
        sidecarsSubject === "provider" &&
        pickerLocation !== null,
    },
  );
  // Topic list for the picked provider, ordered by recency. Disabled
  // until a provider is picked.
  const providerTopicsQuery = trpc.footage.listProviderTopics.useQuery(
    { providerFolderId: pickerProviderFolderId ?? "" },
    {
      enabled:
        pageMode === "sidecars" &&
        sidecarsSubject === "provider" &&
        pickerProviderFolderId !== null,
    },
  );

  // Substring filters for the typeaheads. Fine for clinic-
  // scale lists; if it ever gets slow we'd reach for fuzzy match.
  const filteredPatients = useMemo(() => {
    const all = patientListQuery.data ?? [];
    const q = pickerSearch.trim();
    if (!q) return all;
    return all.filter((p) => matchesSearch(p.name, q));
  }, [patientListQuery.data, pickerSearch]);

  const filteredProviders = useMemo(() => {
    const all = providerListQuery.data ?? [];
    const q = pickerSearch.trim();
    if (!q) return all;
    return all.filter((p) => matchesSearch(p.name, q));
  }, [providerListQuery.data, pickerSearch]);

  const filteredTopics = useMemo(() => {
    const all = providerTopicsQuery.data ?? [];
    const q = pickerTopicSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((t) => t.topic.toLowerCase().includes(q));
  }, [providerTopicsQuery.data, pickerTopicSearch]);

  const pickedPatient =
    pickerPatientFolderId !== null
      ? (patientListQuery.data ?? []).find(
          (p) => p.folderId === pickerPatientFolderId,
        ) ?? null
      : null;

  const pickedProvider =
    pickerProviderFolderId !== null
      ? (providerListQuery.data ?? []).find(
          (p) => p.folderId === pickerProviderFolderId,
        ) ?? null
      : null;

  const handleDownloadProviderSidecars = async () => {
    if (!pickedProvider) return;
    setOverlay({ phase: "downloading", progress: null });
    try {
      const result = await downloadBundle({
        // Sentinel non-empty selectedIds — fetchMetadata is what
        // actually resolves the clip list. The empty-input guard at
        // the top of downloadBundle short-circuits without it.
        selectedIds: [1],
        fetchMetadata: async () =>
          utils.footage.bundleMetadataByProviderTopic.fetch({
            providerFolderId: pickedProvider.folderId,
            topic: pickerTopic,
          }),
        recordDownload: async (idsArg) => {
          await recordDownloadMutation.mutateAsync({ ids: idsArg });
        },
        videoMode: "sidecars-only",
        // Bundle name encodes (provider, topic) so the editor can
        // tell two bundles for the same provider apart at a glance.
        // Falls back to just the provider name when no topic was
        // picked (i.e. provider had no topic_tags yet).
        bundleNamePrefix: pickerTopic
          ? `Sidecars - ${pickedProvider.name} - ${pickerTopic}`
          : `Sidecars - ${pickedProvider.name}`,
        onProgress: (p) => setOverlay({ phase: "downloading", progress: p }),
      });
      // Sidecar bundles carry no video bytes (sidecars-only), so any
      // failures are consent PDFs. Retry re-runs the whole sidecar bundle
      // (cheap — text + PDFs); the cart is untouched here.
      settleOverlay(result, {
        onRetry: result && result.failures.length > 0
          ? () => void handleDownloadProviderSidecars()
          : null,
        clearOnClean: false,
      });
    } catch (err) {
      setOverlay(null);
      throw err;
    }
  };

  const handleDownloadPatientSidecars = async () => {
    if (!pickedPatient) return;
    setOverlay({ phase: "downloading", progress: null });
    try {
      const result = await downloadBundle({
        // selectedIds is unused in this flow — fetchMetadata returns
        // the full clip list by patient. We pass [1] (any non-empty
        // array) so the empty-input guard at the top of downloadBundle
        // doesn't short-circuit.
        selectedIds: [1],
        fetchMetadata: async () =>
          utils.footage.bundleMetadataByPatient.fetch({
            patientFolderId: pickedPatient.folderId,
            safetyFilter: "safe",
            includeCheckBeforePosting: false,
          }),
        recordDownload: async (idsArg) => {
          await recordDownloadMutation.mutateAsync({ ids: idsArg });
        },
        videoMode: "sidecars-only",
        bundleNamePrefix: `Sidecars - ${pickedPatient.name}`,
        onProgress: (p) => setOverlay({ phase: "downloading", progress: p }),
      });
      settleOverlay(result, {
        onRetry: result && result.failures.length > 0
          ? () => void handleDownloadPatientSidecars()
          : null,
        clearOnClean: false,
      });
    } catch (err) {
      setOverlay(null);
      throw err;
    }
  };

  // Triggered from the success screen's "Begin a New Search" button.
  // Clears the cart, resets every search input + filter back to its
  // initial default, and dismisses the overlay — putting the page in
  // the same state as a fresh visit.
  const handleBeginNewSearch = () => {
    clearSelection();
    setInput("");
    setPatientFilters({
      preApprovedNoSI: true,
      preApprovedSI: false,
      cbpNoSI: false,
      cbpSI: false,
    });
    setBoostLesserUsed(false);
    setSubjectKindFilter("all");
    setSelectedRegions(new Set());
    setDisplayedLimit(PAGE_SIZE);
    setPageMode("search");
    setSidecarsSubject("patient");
    setPickerLocation(null);
    setPickerPatientFolderId(null);
    setPickerProviderFolderId(null);
    setPickerTopic(null);
    setPickerSearch("");
    setPickerTopicSearch("");
    setOverlay(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <UpdateBanner />
      <AppHeader />
      {/*
       * Wrapper repaints the --primary accent to Minnesota purple for
       * everything below the header — sparkles icon, search-input focus
       * ring, result-card "Open in Drive" link, etc. Mirrors how Provider
       * A-Roll uses .theme-provider-aroll for Iowa Blue.
       */}
      <div className="theme-footage-search">
        {/* Sticky selection tray — appears only when at least one clip is
            picked AND no overlay is up. The download overlay takes the
            full main area so the tray would just be visual noise. */}
        {selected.size > 0 && !overlay && (
          <SelectionTray
            selected={selected}
            onRemove={removeFromSelection}
            onClear={clearSelection}
            onDownload={handleDownloadBundle}
            includeSidecars={includeSidecars}
            onToggleSidecars={setIncludeSidecars}
          />
        )}
        {overlay ? (
          <DownloadOverlay state={overlay} onBeginNewSearch={handleBeginNewSearch} />
        ) : (
        <main className="container py-8">
          <div className="max-w-3xl mx-auto space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <CardTitle className="text-base">Footage Search</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  Search across video titles, activity &amp; topic tags, AI-generated
                  descriptions, and transcripts. Natural language works —
                  try &ldquo;child working on balance&rdquo; or &ldquo;Babinski test&rdquo;.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {pageMode === "search" && (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Describe what you're looking for..."
                        className="pl-9 pr-9"
                        autoFocus
                      />
                      {/* Inline spinner — present whenever a search
                          query is in flight. Doubles as a hint that
                          previously-shown results may be stale while
                          the new query lands. */}
                      {enabled && searchQuery.isFetching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary animate-spin" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tip: filler words like &ldquo;the&rdquo;, &ldquo;and&rdquo;,
                      &ldquo;of&rdquo; are ignored by default. Wrap a phrase in
                      quotes &mdash; e.g.{" "}
                      <code className="rounded bg-muted px-1 py-0.5">
                        &ldquo;The End&rdquo;
                      </code>{" "}
                      &mdash; to keep them and match filenames literally.
                    </p>
                  </>
                )}
                {/* Four-tab control. First three are subject-kind
                    filters within search mode; the fourth flips the
                    whole page into Sidecars (patient-picker) mode. */}
                <div className="grid grid-cols-4 gap-2">
                  {([
                    {
                      key: "search:all",
                      label: "All Footage",
                      activeClass:
                        "border-primary bg-primary text-primary-foreground",
                      isActive:
                        pageMode === "search" && subjectKindFilter === "all",
                      onClick: () => {
                        setPageMode("search");
                        setSubjectKindFilter("all");
                      },
                    },
                    {
                      key: "search:provider",
                      label: "A-Roll",
                      // Iowa Blue — same as the A-Roll chip on result cards.
                      activeClass:
                        "border-[#5B7C99] bg-[#5B7C99] text-white",
                      isActive:
                        pageMode === "search" && subjectKindFilter === "provider",
                      onClick: () => {
                        setPageMode("search");
                        setSubjectKindFilter("provider");
                      },
                    },
                    {
                      key: "search:patient",
                      label: "B-Roll",
                      // Hardcoded base system green — .theme-footage-search
                      // repaints --primary to MN Purple on this page.
                      activeClass:
                        "border-[#1b6e4a] bg-[#1b6e4a] text-white",
                      isActive:
                        pageMode === "search" && subjectKindFilter === "patient",
                      onClick: () => {
                        setPageMode("search");
                        setSubjectKindFilter("patient");
                      },
                    },
                    {
                      key: "sidecars",
                      label: "Sidecars",
                      // Sidecars mode picks up the page-theme primary
                      // (MN Purple via .theme-footage-search), which
                      // ties it visually to the page's overall accent.
                      activeClass:
                        "border-primary bg-primary text-primary-foreground",
                      isActive: pageMode === "sidecars",
                      onClick: () => setPageMode("sidecars"),
                    },
                  ] as const).map((opt) => {
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={opt.onClick}
                        className={cn(
                          "flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          opt.isActive
                            ? opt.activeClass
                            : "border-border bg-background hover:bg-muted/40 text-foreground",
                        )}
                      >
                        {opt.isActive && <Check className="w-3.5 h-3.5" />}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Patient B-Roll filters — search-mode only. Four
                    independent toggles forming a 2x2 over (permission,
                    has_special_instructions). Provider A-Roll always
                    passes; Private + Home Use Only is never indexed.
                    Sidecars mode hardcodes Pre-Approved (no UI exposed). */}
                {pageMode === "search" && (
                  <>
                    <div className="pt-1">
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Patient B-Roll filters
                      </div>
                      <div className="space-y-2 pl-1">
                        <div>
                          <div className="text-xs font-medium text-foreground mb-1">
                            Pre-Approved
                          </div>
                          <div className="space-y-1 pl-3">
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                              <Checkbox
                                checked={patientFilters.preApprovedNoSI}
                                onCheckedChange={(v) =>
                                  setPatientFilters((prev) => ({
                                    ...prev,
                                    preApprovedNoSI: v === true,
                                  }))
                                }
                              />
                              <span>No Special Instructions</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                              <Checkbox
                                checked={patientFilters.preApprovedSI}
                                onCheckedChange={(v) =>
                                  setPatientFilters((prev) => ({
                                    ...prev,
                                    preApprovedSI: v === true,
                                  }))
                                }
                              />
                              <span>With Special Instructions</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-foreground mb-1">
                            Check Before Posting
                          </div>
                          <div className="space-y-1 pl-3">
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                              <Checkbox
                                checked={patientFilters.cbpNoSI}
                                onCheckedChange={(v) =>
                                  setPatientFilters((prev) => ({
                                    ...prev,
                                    cbpNoSI: v === true,
                                  }))
                                }
                              />
                              <span>No Special Instructions</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                              <Checkbox
                                checked={patientFilters.cbpSI}
                                onCheckedChange={(v) =>
                                  setPatientFilters((prev) => ({
                                    ...prev,
                                    cbpSI: v === true,
                                  }))
                                }
                              />
                              <span>With Special Instructions</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="text-[11px] text-muted-foreground/80 mt-2 flex items-start gap-1.5">
                        <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>
                          Private + Home Use Only footage is excluded from these search results.
                        </span>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none pt-1">
                      <Checkbox
                        checked={boostLesserUsed}
                        onCheckedChange={(v) => setBoostLesserUsed(v === true)}
                      />
                      <span>
                        Boost lesser-used clips — surface fresh material first
                      </span>
                    </label>
                  </>
                )}

                {/* Search-mode: multi-select Location filter. Empty
                    selection = no filter; the label hint communicates
                    that state ("currently showing all") so we don't
                    need a redundant "All Locations" chip. */}
                {pageMode === "search" && (
                  <div className="pt-1">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      Location
                      <span className="opacity-60">
                        {selectedRegions.size === 0
                          ? "— currently showing all"
                          : `— ${selectedRegions.size} selected`}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {LOCATION_CHIP_OPTIONS.map((opt) => {
                        const isSelected = selectedRegions.has(opt.region);
                        return (
                          <button
                            key={opt.region}
                            type="button"
                            onClick={() => toggleRegion(opt.region)}
                            aria-pressed={isSelected}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              isSelected
                                ? `border-transparent text-white ${opt.selectedBg} ring-2 ${opt.selectedRing}`
                                : "border-border bg-background hover:bg-muted/40 text-foreground",
                            )}
                          >
                            {isSelected ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <span className={`w-1.5 h-1.5 rounded-full ${opt.dotClass}`} />
                            )}
                            {opt.region}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Search-mode: "Filmed by" multi-select. Empty selection =
                    any uploader. Populated from footage.listFilmers. */}
                {pageMode === "search" && (filmersQuery.data?.length ?? 0) > 0 && (
                  <div className="pt-1">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <Users className="w-3 h-3" />
                      Filmed by
                      <span className="opacity-60">
                        {selectedFilmers.size === 0
                          ? "— currently showing all"
                          : `— ${selectedFilmers.size} selected`}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(filmersQuery.data ?? []).map((u) => {
                        const isSelected = selectedFilmers.has(u.googleId);
                        return (
                          <button
                            key={u.googleId}
                            type="button"
                            onClick={() => toggleFilmer(u.googleId)}
                            aria-pressed={isSelected}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              isSelected
                                ? "border-transparent text-white bg-sky-600 ring-2 ring-sky-300"
                                : "border-border bg-background hover:bg-muted/40 text-foreground",
                            )}
                          >
                            {isSelected && <Check className="w-3 h-3" />}
                            {u.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Search-mode: Topic multi-select (A-Roll umbrella topics).
                    Hidden when viewing B-Roll only, and when there are no
                    topics yet. Empty selection = any topic. */}
                {pageMode === "search" &&
                  subjectKindFilter !== "patient" &&
                  (topicsQuery.data?.length ?? 0) > 0 && (
                    <div className="pt-1">
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <TagIcon className="w-3 h-3" />
                        Topic
                        <span className="opacity-60">
                          {selectedTopics.size === 0
                            ? "— currently showing all"
                            : `— ${selectedTopics.size} selected`}
                        </span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {(topicsQuery.data ?? []).map((topic) => {
                          const isSelected = selectedTopics.has(topic);
                          return (
                            <button
                              key={topic}
                              type="button"
                              onClick={() => toggleTopic(topic)}
                              aria-pressed={isSelected}
                              className={cn(
                                "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors",
                                "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                isSelected
                                  ? "border-transparent text-white bg-violet-600 ring-2 ring-violet-300"
                                  : "border-border bg-background hover:bg-muted/40 text-foreground",
                              )}
                            >
                              {isSelected && <Check className="w-3 h-3" />}
                              {topic}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Sidecars-mode sub-subject toggle. Decides whether
                    the rest of the picker is Patient-flavored (location
                    → patient → download) or Provider-flavored
                    (location → provider → topic → download). Switching
                    clears all downstream picker state so the user can't
                    accidentally download a patient bundle with provider
                    state lingering or vice versa. */}
                {pageMode === "sidecars" && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {(
                      [
                        {
                          value: "patient",
                          label: "Patients",
                          // Theme green — same hex as the B-Roll tab
                          // above. B-Roll = patient content, so the
                          // sub-toggle mirrors the parent visually.
                          activeClass:
                            "border-[#1b6e4a] bg-[#1b6e4a] text-white",
                        },
                        {
                          value: "provider",
                          label: "Providers",
                          // Iowa Blue — same hex as the A-Roll tab
                          // above. A-Roll = provider content, ditto.
                          activeClass:
                            "border-[#5B7C99] bg-[#5B7C99] text-white",
                        },
                      ] as const
                    ).map((opt) => {
                      const isActive = sidecarsSubject === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (sidecarsSubject === opt.value) return;
                            setSidecarsSubject(opt.value);
                            setPickerLocation(null);
                            setPickerPatientFolderId(null);
                            setPickerProviderFolderId(null);
                            setPickerTopic(null);
                            setPickerSearch("");
                            setPickerTopicSearch("");
                          }}
                          className={cn(
                            "flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            isActive
                              ? opt.activeClass
                              : "border-border bg-background hover:bg-muted/40 text-foreground",
                          )}
                        >
                          {isActive && <Check className="w-3.5 h-3.5" />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Sidecars-mode: single-select Location chips that
                    drive the patient/provider list queries below.
                    Mirrors the upload flow's "pick a location first"
                    progressive disclosure. */}
                {pageMode === "sidecars" && (
                  <div className="pt-1">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      Location
                      <span className="opacity-60">
                        {pickerLocation
                          ? `— ${pickerLocation}`
                          : `— pick one to load ${sidecarsSubject === "patient" ? "patients" : "providers"}`}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {LOCATION_CHIP_OPTIONS.map((opt) => {
                        const isSelected = pickerLocation === opt.region;
                        return (
                          <button
                            key={opt.region}
                            type="button"
                            onClick={() => {
                              // Switching locations resets every
                              // downstream picker state — the list
                              // is about to repopulate from scratch.
                              setPickerLocation(opt.region);
                              setPickerPatientFolderId(null);
                              setPickerProviderFolderId(null);
                              setPickerTopic(null);
                              setPickerSearch("");
                              setPickerTopicSearch("");
                            }}
                            aria-pressed={isSelected}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              isSelected
                                ? `border-transparent text-white ${opt.selectedBg} ring-2 ${opt.selectedRing}`
                                : "border-border bg-background hover:bg-muted/40 text-foreground",
                            )}
                          >
                            {isSelected ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <span className={`w-1.5 h-1.5 rounded-full ${opt.dotClass}`} />
                            )}
                            {opt.region}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Patient flow — search input + PHI-gated patient
                    list + Download button. */}
                {pageMode === "sidecars" && sidecarsSubject === "patient" && (
                  <div className="space-y-2 pt-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        placeholder={
                          pickerLocation
                            ? `Search patients in ${pickerLocation}…`
                            : "Choose a Location above to search…"
                        }
                        className="pl-9"
                        disabled={!pickerLocation}
                      />
                    </div>
                    {pickerLocation && (
                      <div className="rounded border border-border max-h-64 overflow-y-auto">
                        {/* PHI gate — patient names don't render until
                            the user types something. Reduces incidental
                            PHI exposure when someone glances at the
                            screen. */}
                        {pickerSearch.trim().length === 0 ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            Start typing to search patients in {pickerLocation}.
                          </p>
                        ) : patientListQuery.isLoading ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            Loading patients…
                          </p>
                        ) : filteredPatients.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            No patients in {pickerLocation} match &ldquo;{pickerSearch}&rdquo;.
                          </p>
                        ) : (
                          <ul className="divide-y divide-border">
                            {filteredPatients.map((p) => {
                              const isPicked =
                                pickerPatientFolderId === p.folderId;
                              return (
                                <li key={p.folderId}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPickerPatientFolderId(p.folderId)
                                    }
                                    className={cn(
                                      "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors",
                                      isPicked
                                        ? "bg-primary/10 text-foreground"
                                        : "hover:bg-muted/40 text-foreground",
                                    )}
                                  >
                                    {isPicked && (
                                      <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                                    )}
                                    <span className="flex-1 truncate">
                                      {p.name}
                                    </span>
                                    {p.permission && (
                                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                                        {p.permission}
                                      </span>
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                    {pickedPatient && (
                      <button
                        type="button"
                        onClick={handleDownloadPatientSidecars}
                        disabled={overlay !== null}
                        className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download sidecars for {pickedPatient.name}
                      </button>
                    )}
                  </div>
                )}

                {/* Provider flow — search input + PHI-gated provider
                    list, then a topic dropdown sorted by recency, then
                    Download button. The topic step's the bit that
                    diverges from the patient flow: the editor picks a
                    "project" (topic) under the provider before
                    bundling. */}
                {pageMode === "sidecars" && sidecarsSubject === "provider" && (
                  <div className="space-y-2 pt-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        placeholder={
                          pickerLocation
                            ? `Search providers in ${pickerLocation}…`
                            : "Choose a Location above to search…"
                        }
                        className="pl-9"
                        disabled={!pickerLocation}
                      />
                    </div>
                    {pickerLocation && (
                      <div className="rounded border border-border max-h-64 overflow-y-auto">
                        {pickerSearch.trim().length === 0 ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            Start typing to search providers in {pickerLocation}.
                          </p>
                        ) : providerListQuery.isLoading ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            Loading providers…
                          </p>
                        ) : filteredProviders.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">
                            No providers in {pickerLocation} match &ldquo;{pickerSearch}&rdquo;.
                          </p>
                        ) : (
                          <ul className="divide-y divide-border">
                            {filteredProviders.map((p) => {
                              const isPicked =
                                pickerProviderFolderId === p.folderId;
                              return (
                                <li key={p.folderId}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPickerProviderFolderId(p.folderId);
                                      setPickerTopic(null);
                                      setPickerTopicSearch("");
                                    }}
                                    className={cn(
                                      "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors",
                                      isPicked
                                        ? "bg-primary/10 text-foreground"
                                        : "hover:bg-muted/40 text-foreground",
                                    )}
                                  >
                                    {isPicked && (
                                      <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                                    )}
                                    <span className="flex-1 truncate">
                                      {p.name}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Topic dropdown — appears once a provider is
                        picked. Topics aren't PHI so the list shows
                        immediately (no typing gate); ordering is by
                        most-recently-used so the editor's current
                        project floats to the top. */}
                    {pickedProvider && (
                      <div className="space-y-2 pt-1">
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <TagIcon className="w-3 h-3" />
                          Topic
                          <span className="opacity-60">
                            {pickerTopic
                              ? `— ${pickerTopic}`
                              : "— pick a project under this provider"}
                          </span>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={pickerTopicSearch}
                            onChange={(e) =>
                              setPickerTopicSearch(e.target.value)
                            }
                            placeholder="Filter topics…"
                            className="pl-9"
                          />
                        </div>
                        <div className="rounded border border-border max-h-64 overflow-y-auto">
                          {providerTopicsQuery.isLoading ? (
                            <p className="text-xs text-muted-foreground p-3 text-center">
                              Loading topics…
                            </p>
                          ) : filteredTopics.length === 0 ? (
                            providerTopicsQuery.data &&
                            providerTopicsQuery.data.length === 0 ? (
                              <p className="text-xs text-muted-foreground p-3 text-center">
                                No topics yet for this provider. You
                                can still download every clip below
                                using &ldquo;All clips&rdquo;.
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground p-3 text-center">
                                No topics match &ldquo;{pickerTopicSearch}&rdquo;.
                              </p>
                            )
                          ) : (
                            <ul className="divide-y divide-border">
                              {filteredTopics.map((t) => {
                                const isPicked = pickerTopic === t.topic;
                                return (
                                  <li key={t.topic}>
                                    <button
                                      type="button"
                                      onClick={() => setPickerTopic(t.topic)}
                                      className={cn(
                                        "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors",
                                        isPicked
                                          ? "bg-primary/10 text-foreground"
                                          : "hover:bg-muted/40 text-foreground",
                                      )}
                                    >
                                      {isPicked && (
                                        <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                                      )}
                                      <span className="flex-1 truncate">
                                        {t.topic}
                                      </span>
                                      <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
                                        {t.clipCount} clip
                                        {t.clipCount === 1 ? "" : "s"}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground shrink-0">
                                        {formatShortDate(new Date(t.lastSeen))}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>

                        {/* Download button. Enabled when a topic is
                            picked OR when the provider has zero
                            topics (falls back to "all clips" — the
                            server treats pickerTopic=null as no
                            topic filter). */}
                        {(pickerTopic !== null ||
                          (providerTopicsQuery.data &&
                            providerTopicsQuery.data.length === 0)) && (
                          <button
                            type="button"
                            onClick={handleDownloadProviderSidecars}
                            disabled={overlay !== null}
                            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            {pickerTopic
                              ? `Download sidecars — ${pickedProvider.name} · ${pickerTopic}`
                              : `Download sidecars — ${pickedProvider.name} (all clips)`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {pageMode === "search" && !enabled && (
              <p className="text-sm text-muted-foreground text-center py-12">
                Type at least 2 characters to search.
              </p>
            )}

            {/* First-load state — no cached results yet. Big visible
                "thinking" graphic so the user doesn't read the absence
                of result cards as "no matches found". */}
            {enabled && searchQuery.isLoading && (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Loader2 className="w-7 h-7 text-primary animate-spin" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-primary" />
                  </div>
                </div>
                <p className="text-sm font-medium text-foreground">
                  Searching the library…
                </p>
                <p className="text-xs">
                  Checking titles, tags, AI descriptions, and transcripts.
                </p>
              </div>
            )}

            {/* "No matches" — only show when the query truly returned
                nothing AND we're not still refetching, so an in-flight
                query doesn't briefly flash "No matches" before its
                results land. */}
            {enabled &&
              !searchQuery.isLoading &&
              !searchQuery.isFetching &&
              results.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-12">
                  No matches. Try a different query — search runs across titles,
                  tags, AI descriptions, and transcripts.
                </p>
              )}

            {/* Cached results dim slightly while a new query is in flight
                so the user reads the visual change as "these are stale,
                fresh ones loading" rather than "nothing's happening". */}
            <div
              className={cn(
                "space-y-6 transition-opacity",
                searchQuery.isFetching &&
                  !searchQuery.isLoading &&
                  results.length > 0
                  ? "opacity-50"
                  : "opacity-100",
              )}
            >
              {results.map((hit) => (
                <ResultCard
                  key={hit.file.id}
                  hit={hit}
                  queryTokens={queryTokens}
                  selected={selected.has(hit.file.id)}
                  audioIncluded={audioIncluded(hit.file.id)}
                  onToggleSelect={() =>
                    toggleSelection({
                      id: hit.file.id,
                      title: hit.file.renamedName || hit.file.originalName,
                      driveFileId: hit.file.driveFileId,
                      subjectKind: hit.file.subjectKind,
                      mediaKind:
                        hit.file.mediaKind === "audio" ? "audio" : "video",
                    })
                  }
                  onToggleAudioInclusion={() => toggleAudioInclusion(hit.file.id)}
                  onAddSessionMates={() => handleAddSessionMates(hit)}
                  isAddingSessionMates={sessionAddLoadingId === hit.file.sessionId}
                  refetchSearch={() => searchQuery.refetch()}
                />
              ))}
            </div>

            {/* "Show 20 more" — server-side pagination via a larger limit.
                Disabled while a fetch is in flight; goes away when the cap
                or end of results is reached. */}
            {enabled && results.length > 0 && (
              <div className="pt-2 text-center">
                {canShowMore ? (
                  <button
                    type="button"
                    onClick={() =>
                      setDisplayedLimit((l) => Math.min(l + PAGE_SIZE, MAX_RESULTS))
                    }
                    disabled={isFetchingMore}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-background text-sm font-medium hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {isFetchingMore ? "Loading…" : `Show ${PAGE_SIZE} more`}
                  </button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {displayedLimit >= MAX_RESULTS
                      ? `Showing the top ${MAX_RESULTS} results. Refine your search to narrow further.`
                      : `End of results — ${results.length} match${results.length === 1 ? "" : "es"}.`}
                  </p>
                )}
              </div>
            )}
          </div>
        </main>
        )}
      </div>
      {/* Confirmation dialog for the "Add all clips from this session"
          button when the add would bring in more than 10 new clips.
          Only trips for genuinely large sessions — smaller adds skip
          straight to the toast so the flow stays one-click for the
          common case. */}
      <AlertDialog
        open={sessionAddConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setSessionAddConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Add {sessionAddConfirm?.newCount ?? 0} clips to bundle?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This session has {sessionAddConfirm?.newCount ?? 0} clip
              {sessionAddConfirm?.newCount === 1 ? "" : "s"} that
              {" "}aren&apos;t in your bundle yet. Add all of them at once?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = sessionAddConfirm;
                if (!pending) return;
                addManyToSelection(pending.items);
                toast.success(
                  `Added ${pending.newCount} clip${pending.newCount === 1 ? "" : "s"} from this session to the bundle.`,
                );
                setSessionAddConfirm(null);
              }}
            >
              Add {sessionAddConfirm?.newCount ?? 0} clips
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Format a date as a compact recency hint for result cards — full
 * locale date if it's in another year, "Mon DD" if it's this year.
 * Used in the "Used Nx · last <date>" badge.
 */
function formatShortDate(d: Date | string): string {
  const dt = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dt.getTime())) return "?";
  const now = new Date();
  if (dt.getFullYear() !== now.getFullYear()) {
    return dt.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Format bytes as a short human-readable string (e.g. "1.2 MB", "812 KB").
 * Used in the download progress strip so the user can eyeball whether a
 * stall is on a 5 MB clip or a 5 GB one.
 */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

/**
 * Two stacked progress bars rendered inside SelectionTray while a bundle
 * download is in flight:
 *   - Current file: % of bytes received for the file in flight.
 *   - Overall: (completedFiles + currentFileFraction) / totalFiles, so the
 *     overall bar advances smoothly *within* a file rather than jumping
 *     in big chunks at each file boundary.
 *
 * When Content-Length is missing for the current file (rare), the current
 * bar shows an indeterminate-ish state (0%) and we display just the bytes
 * received without a "/ total" denominator.
 */
/**
 * Thin purple-on-muted progress strip used inside the DownloadOverlay.
 * `bg-primary` resolves to MN Purple via `.theme-footage-search`, so we
 * don't reuse the shadcn `Progress` primitive — its track is locked to
 * `bg-secondary`, which we'd want to override anyway.
 */
function ProgressStrip({ fraction }: { fraction: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Two stacked progress bars rendered inside the DownloadOverlay's
 * "downloading" phase. `progress` is null during the brief preparing-
 * metadata window before the first emit fires — we show 0% bars then so
 * the layout doesn't jump when real numbers arrive.
 */
function DownloadProgressBars({ progress }: { progress: BundleProgress | null }) {
  const totalFiles = progress?.totalFiles ?? 0;
  const completed = progress?.completed ?? 0;
  const inFlight = progress?.inFlight ?? [];
  // Overall fraction: completed files + sum of fractional progress on
  // currently-streaming files, divided by total. This makes the
  // overall bar advance smoothly within each file rather than jumping
  // only when one finishes.
  const inFlightFractionSum = inFlight.reduce((sum, f) => {
    if (f.total && f.total > 0) return sum + Math.min(1, f.received / f.total);
    return sum;
  }, 0);
  const overallFraction =
    totalFiles > 0
      ? Math.min(1, Math.max(0, (completed + inFlightFractionSum) / totalFiles))
      : 0;
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-baseline gap-2 text-sm">
          <span className="font-medium">Overall</span>
          <span className="ml-auto tabular-nums text-muted-foreground">
            {completed} / {totalFiles} ({Math.round(overallFraction * 100)}%)
          </span>
        </div>
        <ProgressStrip fraction={overallFraction} />
      </div>
      {inFlight.length > 0 ? (
        <div className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground">
            {inFlight.length} downloading in parallel
          </div>
          {inFlight.map((f) => {
            const fraction =
              f.total && f.total > 0 ? Math.min(1, f.received / f.total) : 0;
            return (
              <div key={f.key} className="space-y-1">
                <div className="flex items-baseline gap-2 text-xs">
                  <span className="truncate min-w-0" title={f.name}>
                    {f.name}
                  </span>
                  <span className="tabular-nums text-muted-foreground shrink-0 ml-auto">
                    {f.total
                      ? `${formatBytes(f.received)} / ${formatBytes(f.total)}`
                      : f.received > 0
                        ? formatBytes(f.received)
                        : "—"}
                  </span>
                  {/* Per-file mid-stream skip — aborts the fetch and
                      replaces the file with a tiny Drive shortcut.
                      Video tasks only; consents have no shortcut
                      equivalent and expose no skip(). */}
                  {f.kind === "video" && f.skip && (
                    <button
                      type="button"
                      onClick={f.skip}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Stop downloading this file and leave a Drive shortcut instead."
                    >
                      Skip → shortcut
                    </button>
                  )}
                </div>
                <ProgressStrip fraction={fraction} />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          {totalFiles > 0 && completed >= totalFiles ? "Wrapping up…" : "Preparing files…"}
        </p>
      )}
    </div>
  );
}

/**
 * Full-area overlay that replaces the Footage Search main content while
 * a bundle download is in flight, and transitions to a celebration
 * screen on clean completion. Mirrors the post-upload Confirmation
 * page's hero pattern (96px circle + 32px primary Film badge, big bold
 * headline, celebration graphic, action buttons) so the two flows feel
 * like siblings.
 *
 * The success screen always renders once a download finishes. On a clean
 * run it's the celebration hero + "Begin a New Search". When some files
 * failed it keeps the "Payload Delivered!" framing (the bundle DID land)
 * but swaps the hero to an amber alert and adds a prominent panel listing
 * each failed file with an Open-in-Drive link and a "Retry failed" button.
 */
function DownloadOverlay({
  state,
  onBeginNewSearch,
}: {
  state:
    | { phase: "downloading"; progress: BundleProgress | null }
    | {
        phase: "success";
        result: BundleResult;
        onRetry: (() => void) | null;
      };
  onBeginNewSearch: () => void;
}) {
  const { user } = useAuth();
  if (state.phase === "downloading") {
    const fileCount = state.progress
      ? `${state.progress.completed} of ${state.progress.totalFiles} files complete`
      : "Preparing files…";
    return (
      <main className="flex-1 container py-12">
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-full bg-primary/15 flex items-center justify-center">
                <Download className="w-12 h-12 text-primary" />
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-foreground tracking-tight">
                Downloading bundle
              </h2>
              <p className="text-muted-foreground mt-2 text-lg">{fileCount}</p>
            </div>
          </div>
          <Card>
            <CardContent className="pt-6">
              <DownloadProgressBars progress={state.progress} />
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const { result, onRetry } = state;
  const clipCount = result.videoSucceeded;
  const consentCount = result.consentSucceeded;
  const skippedCount = result.videoSkipped;
  const convertedCount = result.videoConverted;
  const pluralClip = clipCount === 1 ? "" : "s";
  const pluralConsent = consentCount === 1 ? "" : "s";
  const pluralSkipped = skippedCount === 1 ? "" : "s";
  const pluralConverted = convertedCount === 1 ? "" : "s";
  const failures = result.failures;
  const hasFailures = failures.length > 0;
  const pluralFail = failures.length === 1 ? "" : "s";
  return (
    <main className="flex-1 container py-12">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              {hasFailures ? (
                <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-12 h-12 text-amber-600" />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                </div>
              )}
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Film className="w-4 h-4 text-primary-foreground" />
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-foreground tracking-tight">
              Payload Delivered!
            </h2>
            <p className="text-muted-foreground mt-2 text-lg">
              {clipCount} clip{pluralClip}
              {consentCount > 0
                ? ` and ${consentCount} consent form${pluralConsent}`
                : ""}{" "}
              saved to{" "}
              <span className="font-medium text-foreground">{result.bundleName}</span>.
              {convertedCount > 0 ? (
                <>
                  {" "}
                  <span className="text-sm">
                    ({convertedCount} skipped mid-download → Drive shortcut
                    {pluralConverted}.)
                  </span>
                </>
              ) : null}
              {skippedCount > 0 ? (
                <>
                  {" "}
                  <span className="text-sm">
                    ({skippedCount} local-only clip{pluralSkipped} included as
                    transcripts/descriptions only.)
                  </span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        {hasFailures ? (
          // Prominent failure panel — the whole point of always landing
          // here even on a partial download. Lists each failed file with
          // its error and an Open-in-Drive link (Drive clips + consent
          // PDFs have one; NAS-only rows don't). The retry button re-runs
          // just the failed clips.
          <Card className="border-2 border-amber-400 bg-amber-50">
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <h3 className="text-lg font-semibold">
                  {failures.length} file{pluralFail} didn&apos;t download
                </h3>
              </div>
              <p className="text-sm text-amber-900/80">
                {clipCount + consentCount + result.audioSucceeded > 0
                  ? "Everything else saved to "
                  : "Nothing else was saved to "}
                <span className="font-medium">{result.bundleName}</span>. Retry
                the failed file{pluralFail} below, or open each in Google Drive
                to grab it manually.
              </p>
              <ul className="space-y-2">
                {failures.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-start justify-between gap-3 rounded border border-amber-300 bg-white/70 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground break-all">
                        {f.name}
                      </p>
                      <p className="text-xs text-muted-foreground break-words">
                        {f.error}
                      </p>
                    </div>
                    {f.driveFileId ? (
                      <a
                        href={`https://drive.google.com/file/d/${f.driveFileId}/view`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      >
                        Open in Drive
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <span className="shrink-0 text-xs italic text-muted-foreground">
                        NAS-only — no Drive link
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : (
          <div className="flex justify-center">
            <img
              src={user?.successGraphicUrl ?? DEFAULT_SUCCESS_GRAPHIC}
              alt="The Footage Rambo Team — bundle ready!"
              className="max-w-3xl max-h-[420px] w-auto h-auto rounded-2xl shadow-lg"
            />
          </div>
        )}
        <div className="flex justify-center gap-3">
          {onRetry ? (
            <Button
              variant="outline"
              onClick={onRetry}
              className="h-12 px-6 text-base font-semibold flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry failed download{pluralFail}
            </Button>
          ) : null}
          <Button
            onClick={onBeginNewSearch}
            className="h-12 px-8 text-base font-semibold flex items-center gap-2"
          >
            Begin a New Search
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </main>
  );
}

/**
 * Pinned-to-top summary of currently-selected clips. Always visible while
 * scrolling once at least one clip is picked. Expandable to show the full
 * list of titles with per-item remove buttons. The "Download bundle"
 * action lands in Phase 4 — for now the tray exposes count + clear +
 * list inspection so the user can manage their accumulating picks.
 */
function SelectionTray({
  selected,
  onRemove,
  onClear,
  onDownload,
  includeSidecars,
  onToggleSidecars,
}: {
  selected: Map<number, SelectedItem>;
  onRemove: (id: number) => void;
  onClear: () => void;
  onDownload: () => void | Promise<void>;
  includeSidecars: boolean;
  onToggleSidecars: (v: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const items = Array.from(selected.values());
  const aRoll = items.filter((it) => it.subjectKind === "provider").length;
  const bRoll = items.filter((it) => it.subjectKind === "patient").length;
  const unclassified = items.length - aRoll - bRoll;

  // Display order: A-Roll first (provider clips set the visual lead),
  // then B-Roll, then anything unclassified. Within each kind, newest
  // selection floats to the top — Array.prototype.sort is stable in
  // modern JS, so reversing first preserves the recency order
  // inside each kind. The selection Map keeps insertion order, so
  // reversing flips it to newest-first.
  const rank = (kind: string | null): number => {
    if (kind === "provider") return 0;
    if (kind === "patient") return 1;
    return 2;
  };
  const displayItems = [...items]
    .reverse()
    .sort((a, b) => rank(a.subjectKind) - rank(b.subjectKind));

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="sticky top-0 z-30 bg-primary text-primary-foreground shadow-md">
      <div className="container py-2.5 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          <span>
            {items.length} clip{items.length === 1 ? "" : "s"} selected
          </span>
          <span className="text-xs opacity-80">
            ({aRoll} A-Roll, {bRoll} B-Roll
            {unclassified > 0 ? `, ${unclassified} unclassified` : ""})
          </span>
        </button>
        <div className="flex-1" />
        <label
          className="inline-flex items-center gap-1.5 text-xs opacity-90 cursor-pointer select-none"
          title="Videos, consent forms, and the manifest always download. Uncheck to leave out the .description.txt / .txt / .srt sidecar files."
        >
          <input
            type="checkbox"
            checked={includeSidecars}
            onChange={(e) => onToggleSidecars(e.target.checked)}
            disabled={downloading}
            className="accent-primary-foreground"
          />
          Include sidecars
        </label>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded bg-primary-foreground/15 hover:bg-primary-foreground/25 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {downloading ? "Downloading…" : "Download bundle"}
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={downloading}
          className="text-xs underline hover:no-underline opacity-90 disabled:opacity-40"
        >
          Clear all
        </button>
      </div>
      {expanded && (
        <div className="container pb-3 max-h-64 overflow-y-auto">
          <ul className="space-y-1 text-sm">
            {displayItems.map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-2 bg-primary-foreground/10 rounded px-2 py-1"
              >
                <span className="text-[10px] uppercase tracking-wider opacity-80 shrink-0">
                  {it.subjectKind === "provider"
                    ? "A"
                    : it.subjectKind === "patient"
                      ? "B"
                      : "?"}
                </span>
                <span className="flex-1 truncate" title={it.title}>
                  {it.title}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(it.id)}
                  aria-label={`Remove ${it.title}`}
                  className="p-0.5 rounded hover:bg-primary-foreground/20"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type RouterOutputs = inferRouterOutputs<AppRouter>;
type SearchHit = RouterOutputs["footage"]["search"][number];

/**
 * Drive-style location chip — colored dot + region label on a neutral
 * pill, mirrors the Location picker in PatientStep so the same color
 * vocabulary applies to "where did this clip come from". Returns null
 * for legacy rows whose region hasn't been backfilled yet — they just
 * don't show a Location chip until the backfill runs.
 */
function regionBadge(region: string | null): {
  label: string;
  dotClass: string;
  textClass: string;
} | null {
  switch (region) {
    case "Iowa":
      return {
        label: "Iowa",
        dotClass: "bg-[#5B7C99]",
        textClass: "text-[#5B7C99]",
      };
    case "Minnesota":
      return {
        label: "Minnesota",
        dotClass: "bg-[#6A5ACD]",
        textClass: "text-[#6A5ACD]",
      };
    case "Colorado":
      return {
        label: "Colorado",
        dotClass: "bg-[#1b6e4a]",
        textClass: "text-[#1b6e4a]",
      };
    default:
      return null;
  }
}

/**
 * Drive-style permission chip for a B-roll clip — colored dot + label
 * on a neutral pill, matching the chips in GDriveFolderPicker.tsx so
 * the visual vocabulary stays consistent across the app. Unknown
 * permission (legacy row pending backfill) shows a generic gray
 * "B-Roll" pill with no dot.
 */
function permissionBadge(permission: string | null): {
  label: string;
  dotClass: string | null;
  textClass: string;
} {
  switch (permission) {
    case "pre-approved":
      return {
        label: "Pre-Approved",
        dotClass: "bg-emerald-500",
        textClass: "text-emerald-700 dark:text-emerald-400",
      };
    case "check-before-posting":
      return {
        label: "Check Before Posting",
        dotClass: "bg-[#4986e7]",
        textClass: "text-[#4986e7]",
      };
    case "private-internal":
      return {
        label: "Private + Internal Use Only",
        dotClass: "bg-red-500",
        textClass: "text-red-600 dark:text-red-400",
      };
    default:
      return {
        label: "B-Roll",
        dotClass: null,
        textClass: "text-muted-foreground",
      };
  }
}

function ResultCard({
  hit,
  queryTokens,
  selected,
  audioIncluded,
  onToggleSelect,
  onToggleAudioInclusion,
  onAddSessionMates,
  isAddingSessionMates,
  refetchSearch,
}: {
  hit: SearchHit;
  queryTokens: string[];
  selected: boolean;
  /** Whether the "Include accompanying audio" toggle is on for this
   *  video row. Always false for audio rows (audio is the attachment,
   *  not the host). */
  audioIncluded: boolean;
  onToggleSelect: () => void;
  onToggleAudioInclusion: () => void;
  /** Fires the parent's session-mates fetch + cart add flow.
   *  Confirmation prompt for large additions is handled at the parent
   *  level, so the card just calls this and stays passive. */
  onAddSessionMates: () => void;
  /** True while the parent is fetching session mates for THIS card's
   *  session — used to show a spinner and disable the button so a
   *  double-click can't fire two overlapping requests. */
  isAddingSessionMates: boolean;
  /** Called after a successful Retry AI run resolves "done", so the
   *  newly-saved description / transcript / embedding land back in the
   *  visible list without the user having to re-run their search. */
  refetchSearch: () => void;
}) {
  const { file, matchedOn, rrfScore } = hit;
  const isAudio = file.mediaKind === "audio";
  // Oversized "reference-only" clip: a giant camera master too large to upload.
  // Only its extracted audio (transcript) + a still frame were indexed; the
  // full file lives on the NAS. No inline playback, no byte download — the
  // card shows the still + the NAS path, and bundles get a pointer instead.
  const isReference = file.referenceOnly;
  const accompanyingAudio = hit.accompanyingAudio ?? [];
  const [showTranscript, setShowTranscript] = useState(false);
  const { enqueuePegasusPoll } = useUploadContext();
  /**
   * Local Retry-AI state machine. "kicking" covers the brief POST to
   * /api/retry-ai-analysis (usually <200 ms — the server returns 202
   * immediately and runs the actual work in the background). "running"
   * covers the much longer server-side Pegasus + Whisper + embed wait,
   * during which the Pegasus poll fires the eventual batch-toast and
   * (on success) the refetchSearch swap. "done" / "failed" persist
   * after the poll resolves so the row keeps a small inline confirmation
   * until the user re-searches.
   */
  const [retryState, setRetryState] = useState<"idle" | "kicking" | "running" | "done" | "failed">("idle");

  const handleRetryAi = async () => {
    if (retryState === "kicking" || retryState === "running") return;
    setRetryState("kicking");
    try {
      const res = await fetch("/api/retry-ai-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ footageFileId: file.id }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({} as { error?: string }));
        const msg = (errBody as { error?: string }).error ?? `Retry failed (${res.status})`;
        toast.error(`Retry AI failed: ${msg}`);
        setRetryState("failed");
        return;
      }
      // Server accepted — the actual AI work now runs in the background.
      // Plug into the existing Pegasus poll so the batch-completion toast
      // automatically surfaces the outcome, and refetch the search list
      // on success so the new description appears in place.
      setRetryState("running");
      toast.info(
        `Re-running AI analysis on "${file.renamedName || file.originalName}". You'll get a toast when it finishes.`,
        { duration: 5000 },
      );
      enqueuePegasusPoll({
        footageFileId: file.id,
        displayName: file.renamedName || file.originalName,
        sidecarName: null,
        dateFolderHandle: null,
        onStatusChange: (status) => {
          if (status === "done") {
            setRetryState("done");
            refetchSearch();
          } else {
            setRetryState("failed");
          }
        },
      });
    } catch (err) {
      toast.error(
        `Retry AI failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      setRetryState("failed");
    }
  };
  // Preview is viewport-gated. Off-screen cards render an empty player
  // shell (no src, preload="none") and fetch nothing; once a card scrolls
  // near the viewport (useNearViewport) the src is set with
  // preload="metadata" so the browser fetches just enough to render a
  // poster frame, not the full bytes — actual byte streaming only starts
  // when the user hits play on a specific clip.
  // Aspect ratio captured from the loaded video metadata so portrait /
  // square clips don't sit letterboxed inside a forced 16:9 container.
  // Defaults to "16 / 9" until the metadata event fires; the container
  // CSS uses max-width + max-height together so each orientation fits
  // sensibly without overflowing the card width.
  const [aspectRatio, setAspectRatio] = useState<string>("16 / 9");
  // Only one clip plays at a time; clips pause when scrolled out of view.
  const { setMediaRef, onPlay } = useCoordinatedMedia();
  // Defer this card's media fetch until it's near the viewport (see
  // useNearViewport). Off-screen cards render an empty player shell and
  // fetch nothing, so a long results page doesn't stampede the proxy.
  const { ref: previewBoxRef, near: previewNear } = useNearViewport<HTMLDivElement>();
  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.videoWidth > 0 && v.videoHeight > 0) {
      setAspectRatio(`${v.videoWidth} / ${v.videoHeight}`);
    }
  };

  const title = file.renamedName || file.originalName;
  const tags = [...(file.activities ?? []), ...(file.topicTags ?? [])];
  // Transcripts can be many KB long — show only a window around the
  // matched query tokens. AI descriptions are short enough (a few
  // hundred chars typically) that we show them in full for both
  // A-Roll and B-Roll; the side-by-side layout has room and the user
  // explicitly asked to read the whole thing rather than chase a
  // truncated snippet.
  const transcriptSnippet = snippetAround(file.transcriptText, queryTokens, 240);
  const driveLink = file.driveFileId
    ? `https://drive.google.com/file/d/${file.driveFileId}/view`
    : null;
  // Inline preview streams through our server-side proxy
  // (/api/footage/stream). For Drive-backed rows the proxy uses the
  // app's existing Drive OAuth token, so the user doesn't have to
  // re-sign into Google in the iframe context. For NAS-sourced rows
  // (no driveFileId, but a nasPath) it proxies to the NAS's playback
  // file server over Tailscale instead. Either way, Range requests
  // pass through end-to-end so seek/scrub works.
  // Reference-only rows are never streamed (the nasPath points at a giant
  // master we deliberately never move) — force no preview src so the card
  // renders the still frame + NAS path instead of a broken player. The
  // server also refuses to stream these (409), so this is belt-and-braces.
  const previewSrc =
    !isReference && (file.driveFileId || file.nasPath)
      ? `/api/footage/stream?fileId=${file.id}`
      : null;
  // A-Roll uses Iowa Blue — the same slate-gray-blue as the Provider
  // A-Roll page theme. Deliberately distinct from both the bright blue
  // of Check Before Posting (#4986e7) and Tailwind's primary blue, so
  // the workflow-vs-permission distinction reads at a glance.
  // B-Roll's chip mirrors Drive's permission color. Same dot+label
  // shape for everything below so the row reads consistently.
  const subjectBadge =
    file.subjectKind === "provider"
      ? {
          label: "A-Roll",
          dotClass: "bg-[#5B7C99]",
          textClass: "text-[#5B7C99]",
        }
      : file.subjectKind === "patient"
        ? permissionBadge(file.patientPermission)
        : null;
  // SI is independent of permission tier — render alongside when set.
  const showSIBadge = file.subjectKind === "patient" && file.hasSpecialInstructions;
  // Location chip — renders for both patient and provider clips when
  // we have a region. Legacy rows without one stay clean (no chip).
  const locationBadge = regionBadge(file.region);

  return (
    <Card
      className={
        selected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : undefined
      }
    >
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${title}`}
            className="mt-1 shrink-0"
          />
          <CardTitle className="text-base leading-tight break-words flex-1">
            {title}
          </CardTitle>
          {locationBadge && (
            <Badge
              variant="outline"
              className={`text-[11px] font-medium shrink-0 inline-flex items-center gap-1.5 px-2 ${locationBadge.textClass}`}
              title={`Location: ${locationBadge.label}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${locationBadge.dotClass}`} />
              {locationBadge.label}
            </Badge>
          )}
          {subjectBadge && (
            <Badge
              variant="outline"
              className={`text-[11px] font-medium shrink-0 inline-flex items-center gap-1.5 px-2 ${subjectBadge.textClass}`}
            >
              {subjectBadge.dotClass && (
                <span className={`w-1.5 h-1.5 rounded-full ${subjectBadge.dotClass}`} />
              )}
              {subjectBadge.label}
            </Badge>
          )}
          {showSIBadge && (
            <Badge
              variant="outline"
              className="text-[11px] font-medium shrink-0 inline-flex items-center gap-1.5 px-2 text-[#ff7537]"
              title="This patient folder lives in a **Special Instructions subfolder — review any posting guidance before using."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff7537]" />
              Special Instructions
            </Badge>
          )}
          {isAudio && (
            <Badge
              variant="outline"
              className="text-[11px] font-medium shrink-0 inline-flex items-center gap-1.5 px-2 text-amber-700"
              title="Audio recording — no video. Transcript and AI description still apply."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Audio
            </Badge>
          )}
          {isReference && (
            <Badge
              variant="outline"
              className="text-[11px] font-medium shrink-0 inline-flex items-center gap-1.5 px-2 text-rose-700"
              title="Oversized camera master — too large to upload. Only its audio (transcript) and a still frame are indexed. No inline playback or file download; the bundle includes a pointer to its NAS path."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
              Oversized — reference only
            </Badge>
          )}
          <Badge variant="outline" className="text-xs font-mono shrink-0">
            {rrfScore.toFixed(3)}
          </Badge>
        </div>
        {/* Sub-header line under the title: patient/provider name +
            matched-on chips + download-history badge. Filter out the
            "title" matchedOn chip — the title is right above so
            calling out the match is redundant. Keep tags /
            description / transcript chips: those tell the user where
            a NON-visible match landed. The download-history badge
            always renders ("Not previously downloaded" when count =
            0, "Used Nx · last <date>" otherwise) so this row is
            never empty. */}
        {(() => {
          const visibleMatchedOn = matchedOn.filter((m) => m !== "title");
          return (
            <div className="ml-7 mt-1 flex flex-wrap items-center gap-2">
              {file.subjectName && (
                <span className="text-xs text-muted-foreground">
                  {file.subjectKind === "provider" ? "Provider:" : "Patient:"}{" "}
                  <span className="font-medium text-foreground/80">
                    {file.subjectName}
                  </span>
                </span>
              )}
              {file.providers && file.providers.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  Providers:{" "}
                  <span className="font-medium text-foreground/80">
                    {file.providers.join(", ")}
                  </span>
                </span>
              )}
              {file.videoTopic && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <TagIcon className="w-3 h-3" />
                  <span className="font-medium text-foreground/80">
                    {file.videoTopic}
                  </span>
                </span>
              )}
              {file.uploadedByName && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Filmed by{" "}
                  <span className="font-medium text-foreground/80">
                    {file.uploadedByName}
                  </span>
                </span>
              )}
              {visibleMatchedOn.map((m) => (
                <span
                  key={m}
                  className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded"
                >
                  {m}
                </span>
              ))}
              {/* ml-auto pushes the download-history badge to the
                  right edge of the row so it lines up vertically
                  under the rrf-score pill in the title row above. */}
              {file.usageCount > 0 ? (
                <Badge
                  variant="outline"
                  className="text-[10px] font-medium shrink-0 ml-auto text-muted-foreground inline-flex items-center gap-1"
                  title={
                    file.lastDownloadedAt
                      ? `Included in ${file.usageCount} prior bundle download${file.usageCount === 1 ? "" : "s"}. Last downloaded ${new Date(file.lastDownloadedAt).toLocaleString()}.`
                      : `Included in ${file.usageCount} prior bundle download${file.usageCount === 1 ? "" : "s"}`
                  }
                >
                  <span>Used {file.usageCount}×</span>
                  {file.lastDownloadedAt && (
                    <span className="opacity-70">
                      · last {formatShortDate(file.lastDownloadedAt)}
                    </span>
                  )}
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="text-[10px] font-medium shrink-0 ml-auto text-muted-foreground/70 border-dashed"
                  title="This clip hasn't been included in a Footage Search bundle download yet."
                >
                  Not previously downloaded
                </Badge>
              )}
            </div>
          );
        })()}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* One-click add every clip that was uploaded in the same
            session as this one — useful for pulling in additional
            takes and B-Roll siblings for an intended video without
            re-searching. Respects the current permission filters;
            a confirm prompt fires at the parent level when the add
            would bring in more than 10 new clips. */}
        <button
          type="button"
          onClick={onAddSessionMates}
          disabled={isAddingSessionMates}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium border bg-primary/5 text-primary border-primary/30 hover:bg-primary/10 disabled:opacity-60 disabled:cursor-wait transition-colors"
          title="Add every clip that was uploaded in the same session as this one to the bundle."
        >
          {isAddingSessionMates ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <FolderPlus className="w-3.5 h-3.5" />
          )}
          {isAddingSessionMates ? "Loading session…" : "Add all clips from this session"}
        </button>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t, idx) => (
              <Badge key={`${t}-${idx}`} variant="secondary" className="text-xs">
                <TagIcon className="w-3 h-3 mr-1" />
                {t}
              </Badge>
            ))}
          </div>
        )}

        {/* Video preview + AI description side-by-side so the user can
            scan both at once. Stacks below md (video on top,
            description beneath) when the screen runs out of room.
            Both A-Roll and B-Roll show the FULL Pegasus description
            — the side-by-side layout has room for it, and seeing the
            whole thing beats chasing a query-windowed snippet. Query
            terms still highlight via <Highlight>, so "where did it
            match" is preserved without truncation.
            Both columns ALWAYS render — when a side has no data we
            show a placeholder explaining why, so the layout stays
            consistent and data gaps (local-only uploads with no Drive
            copy, or clips that haven't been AI-analyzed yet) are
            visible at a glance. */}
        <div className="flex flex-col md:flex-row gap-4 items-start">
          {isReference ? (
            // Oversized reference clip — still frame (if we grabbed one) plus
            // a plain-language notice that there's no playback/download and
            // where the real file lives. No <video>: the master never left
            // the NAS.
            <div
              className="w-full md:w-auto shrink-0 overflow-hidden rounded border border-rose-300/70 bg-rose-50/40"
              style={{ maxWidth: "28rem" }}
            >
              {file.stillFrameUrl ? (
                <img
                  src={file.stillFrameUrl}
                  alt={`Still frame from ${title}`}
                  className="w-full h-auto block bg-black"
                  loading="lazy"
                />
              ) : (
                <div className="flex items-center justify-center px-6 py-10 text-xs text-muted-foreground italic text-center">
                  No still frame available for this clip.
                </div>
              )}
              <div className="p-3 space-y-1.5 border-t border-rose-300/70">
                <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-wide">
                  Oversized — reference only
                </p>
                <p className="text-xs text-muted-foreground leading-snug">
                  This camera master is too large to upload, so there's{" "}
                  <span className="font-medium text-foreground/80">
                    no inline playback and no file in downloads
                  </span>
                  . Its audio was transcribed and this still indexed so it stays
                  searchable. Bundle downloads include a{" "}
                  <span className="font-medium text-foreground/80">
                    pointer to the NAS path in the manifest
                  </span>
                  , not the video — retrieve the full file from the NAS at:
                </p>
                {file.nasPath ? (
                  <p className="text-[11px] font-mono text-foreground/80 break-all bg-background/60 rounded px-2 py-1 border border-border">
                    {file.nasPath}
                  </p>
                ) : (
                  <p className="text-[11px] italic text-muted-foreground">
                    NAS path unavailable for this row.
                  </p>
                )}
              </div>
            </div>
          ) : previewSrc ? (
            isAudio ? (
              // Audio row — <audio> doesn't need an aspect-ratio container.
              // The same /api/footage/stream proxy serves the bytes; the
              // browser picks the right codec from the response headers.
              <div
                ref={previewBoxRef}
                className="w-full md:w-auto shrink-0 overflow-hidden rounded border border-border bg-muted/30 p-3 flex items-center"
                style={{ maxWidth: "28rem", minWidth: "16rem" }}
              >
                <audio
                  ref={setMediaRef}
                  src={previewNear ? previewSrc : undefined}
                  className="w-full"
                  controls
                  preload={previewNear ? "metadata" : "none"}
                  onPlay={onPlay}
                  title={`Preview: ${title}`}
                />
              </div>
            ) : (
              <div
                ref={previewBoxRef}
                className="w-full md:w-auto shrink-0 overflow-hidden rounded border border-border bg-black"
                style={{
                  aspectRatio,
                  maxWidth: "28rem",
                  maxHeight: "20rem",
                }}
              >
                <video
                  ref={setMediaRef}
                  src={previewNear ? previewSrc : undefined}
                  className="w-full h-full"
                  controls
                  preload={previewNear ? "metadata" : "none"}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={onPlay}
                  title={`Preview: ${title}`}
                />
              </div>
            )
          ) : (
            <div
              className="w-full md:w-auto shrink-0 flex items-center justify-center rounded border border-dashed border-border bg-muted/30 px-6 py-10 text-xs text-muted-foreground italic text-center"
              style={{ maxWidth: "28rem", minWidth: "16rem" }}
            >
              No preview available for this upload.
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-muted-foreground mb-1">
              AI description
            </p>
            {file.aiDescription ? (
              <div className="text-sm">
                <Highlight text={file.aiDescription} tokens={queryTokens} />
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground italic">
                  {retryState === "running"
                    ? "Re-running AI analysis — this may take a few minutes for large clips."
                    : retryState === "failed"
                      ? "AI description not yet generated. Last retry attempt failed."
                      : retryState === "done"
                        ? "Analysis completed — re-search to view the new description."
                        : "AI description not yet generated."}
                </p>
                {/* Per-row retry. Shown whenever the row is missing an AI
                    description AND has a Drive copy to re-download from.
                    Local-only rows can't be retried in-place — they'd need
                    to be re-uploaded. The button stays visible after
                    failure so the user can try again; goes away on
                    success after the refetchSearch swap re-renders this
                    same card with a populated aiDescription. */}
                {file.driveFileId && (
                  <button
                    type="button"
                    onClick={handleRetryAi}
                    disabled={retryState === "kicking" || retryState === "running"}
                    className={cn(
                      "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium border transition-colors",
                      retryState === "kicking" || retryState === "running"
                        ? "bg-violet-50 text-violet-700 border-violet-200 cursor-wait"
                        : retryState === "failed"
                          ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                          : "bg-primary/5 text-primary border-primary/30 hover:bg-primary/10",
                    )}
                    title="Re-run Pegasus + Whisper + embedding on this clip."
                  >
                    {retryState === "kicking" || retryState === "running" ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    <Sparkles className="w-3 h-3" />
                    {retryState === "kicking"
                      ? "Kicking off…"
                      : retryState === "running"
                        ? "Analyzing in background…"
                        : retryState === "failed"
                          ? "Retry AI (try again)"
                          : "Retry AI"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {transcriptSnippet && (
          <div>
            <button
              type="button"
              onClick={() => setShowTranscript((v) => !v)}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <FileText className="w-3 h-3" />
              {showTranscript ? "Hide" : "Show"} transcript match
            </button>
            {showTranscript && (
              <div className="mt-1">
                <Highlight text={transcriptSnippet} tokens={queryTokens} />
              </div>
            )}
          </div>
        )}

        {/* "Include accompanying audio (N)" — only on video cards that
            have at least one matched audio file in the same subject
            scope. Toggling adds those audio IDs to the bundle download;
            they land in a sibling Audio - MM.DD.YY/ folder next to the
            Footage bundle, with matching sidecars. */}
        {!isAudio && accompanyingAudio.length > 0 && (
          <label
            className={cn(
              "flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors",
              audioIncluded
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-muted/30 hover:bg-muted/50",
            )}
            title={accompanyingAudio
              .map(
                (a) =>
                  `${a.renamedName} (${(a.similarity * 100).toFixed(0)}% match)`,
              )
              .join("\n")}
          >
            <Checkbox
              checked={audioIncluded}
              onCheckedChange={onToggleAudioInclusion}
              aria-label="Include accompanying audio file(s) in bundle"
              className="mt-0.5 shrink-0"
            />
            <span className="flex-1">
              <span className="font-medium">
                Include accompanying audio file
                {accompanyingAudio.length === 1
                  ? ""
                  : `s (${accompanyingAudio.length})`}
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {accompanyingAudio.length === 1
                  ? `${accompanyingAudio[0].renamedName} — `
                  : ""}
                downloaded into a sibling Audio - MM.DD.YY/ folder.
              </span>
            </span>
          </label>
        )}

        {/* "Open in Drive" lives below the preview now — secondary to
            the inline player, but still one click away when needed. */}
        {driveLink && (
          <a
            href={driveLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open in Drive
          </a>
        )}
      </CardContent>
    </Card>
  );
}

function tokenize(query: string): string[] {
  return Array.from(
    new Set(
      query
        .toLowerCase()
        .split(/[^a-z0-9]+/i)
        .filter((t) => t.length >= 3),
    ),
  );
}

/**
 * Pull a window of text around the first occurrence of any query token.
 * Falls back to the leading slice when nothing matches — useful for showing
 * a teaser even when the match was vector-only (semantic without a literal
 * token overlap).
 */
function snippetAround(
  text: string | null,
  tokens: string[],
  windowChars: number,
): string | null {
  if (!text) return null;
  const lower = text.toLowerCase();
  let idx = -1;
  for (const t of tokens) {
    const found = lower.indexOf(t);
    if (found !== -1 && (idx === -1 || found < idx)) idx = found;
  }
  if (idx === -1) {
    return text.length > windowChars ? text.slice(0, windowChars) + "..." : text;
  }
  const start = Math.max(0, idx - Math.floor(windowChars / 4));
  const end = Math.min(text.length, start + windowChars);
  const prefix = start > 0 ? "..." : "";
  const suffix = end < text.length ? "..." : "";
  return prefix + text.slice(start, end) + suffix;
}

function Highlight({ text, tokens }: { text: string; tokens: string[] }) {
  if (tokens.length === 0) {
    return <p className="text-sm text-foreground whitespace-pre-wrap">{text}</p>;
  }
  const pattern = new RegExp(
    `(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );
  const parts = text.split(pattern);
  return (
    <p className="text-sm text-foreground whitespace-pre-wrap">
      {parts.map((part, i) =>
        pattern.test(part) ? (
          <mark key={i} className="bg-yellow-200/60 dark:bg-yellow-500/40 rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}
